import { Separator } from "@/components/ui/separator";
import { Dot, EllipsisVertical, User, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { createBookingTable, createRoom, createTable, getAllRooms, getAllRoomsWithoutPagination, getAllTablesWithRoom, quickBookingTable, updateRoom, updateTable } from "@/api/table-floor/tableFloor";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllTables } from "@/redux/slice/table-slice/table-slice";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { getAllStaff } from "@/api/staff";
import { restaurant_id } from "@/api/lib/url";
import { fetchAllStaff } from "@/redux/slice/staff-management/staff-attendance";

const addNewFloorCardSchema = z.object({
  customer_name: z.string().optional(),
  // treat empty string as "not provided" so email validation runs only when a value is entered
  customer_email: z.preprocess(
    (val) => (typeof val === "string" && val.trim() === "" ? undefined : val),
    z.string().email("Invalid email format").optional()
  ),
  customer_number: z.string().optional(),
  table_id: z.number().min(1, "Table is required"),
  guest: z.coerce.number().min(1, "Guest is required"),
  staff_id: z.coerce.number().optional(),
  end_time: z.string().optional(),
});

const QuickTableModal = ({ isOpen, onClose, data }) => {
  
  const { tables, floors, selectedFloor, selectedRoom } = useSelector(
    (state) => state.table.table
  );
  const { staffList } = useSelector(
      (state) => state.staffManage.attendance
    );
  const [loading, setLoading] = useState(false);
  const [showIsChild, setShowIsChild] = useState(false);
  const [debugPayload, setDebugPayload] = useState(null); // show submitted data preview
  const { user } = useAuth();
  const [customer, setCustomer] = useState([])
  const [rooms, setRooms] = useState([])
  // const [tables, setTables] = useState([])
  // console.log(customer);
  // console.log(rooms);
  
  
  const [selectedPayerUser, setSelectedPayerUser] = useState([]);
  const dispatch = useDispatch();  

  const form = useForm({
    resolver: zodResolver(addNewFloorCardSchema),
    mode: "onChange",
    defaultValues: {
      customer_name: "",
      customer_email: "",
      customer_number: "",
      table_id: "",
      guest: "",
      staff_id: "",
      end_time: "",
    },
  });

  useEffect(() => {
    if (showIsChild && staffList.length === 0) {
      dispatch(fetchAllStaff());
    }
  }, [showIsChild])


  const { reset, formState: { errors }, handleSubmit, register, setValue } = form;

  // When modal opens, pre-fill the required fields (table_id and default guest)
  useEffect(() => {
    if (data) {
      // data contains the table object passed from LiveBookingDetails
      const defaults = {
        customer_name: data?.customer?.full_name || "",
        customer_email: data?.customer?.email || "",
        customer_number: data?.customer?.number || "",
        table_id: data?.id || data?.table_id || "",
        guest: 1,
        staff_id: "",
        end_time: "",
      };
      reset(defaults);
    }
  }, [data, reset]);

  // show validation errors as a toast when user clicks Confirm but form is invalid
  const onInvalidSubmit = (formErrors) => {
    console.log("validation errors", formErrors);
    const firstError = Object.values(formErrors)[0]?.message || "Please fix the highlighted fields";
    toast.error(firstError);
  };

  const addQuickBooking = async (data) => {
    // show validation errors in console for debugging
    console.log(errors);
    console.log(data);
    
    setLoading(true);

    const payload =
      {
        ...data,
        staff_id: showIsChild ? data.staff_id : null,
        restaurant_id: user.restaurant_id,
      };

    // expose submitted payload in the UI so user sees what was sent
    setDebugPayload(payload);

    try {
      let res;
      res = await quickBookingTable(payload);
      if (res.status === "success") {
        toast.success("Booked Table successfully!");
        console.log(res);
        onClose();
        reset();
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Floor Card");
    } finally {
      setLoading(false);
      // clear debug preview after API attempt
      setDebugPayload(null);
    }
  };

  // console.log(form.formState.errors);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <Card className="bg-cblack4 text-cwhite w-[600px] h-[700px] overflow-scroll">
          <CardHeader className="flex flex-row justify-between items-center">
            <CardTitle className="text-cwhite text-xl font-medium">Quick Booking</CardTitle>
            <X onClick={onClose} className="cursor-pointer" size={16} />
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-8">
              <Form {...form}>
                <form onSubmit={handleSubmit(addQuickBooking, onInvalidSubmit)} className="flex flex-col gap-5">
                  {/* ensure table_id is registered so validation passes (pre-filled from `data`) */}
                  <input type="hidden" {...register("table_id")} />
                      <div>
                          <label className="text-sm font-medium">Phone Number</label>
                          <input
                            name="customer_number"
                            value={form.watch("customer_number")}
                            onChange={(e) => form.setValue("customer_number", e.target.value)}
                            className={` mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none disabled:opacity-50 md:text-sm `}
                          />
                          {errors.customer_number && (
                            <p className="text-cred text-sm mt-1">{errors.customer_number.message}</p>
                          )}
                      </div>
                      <div>
                          <label className="text-sm font-medium">Customer Name</label>
                          <input
                              name="customer_name"
                              value={form.watch("customer_name")}
                              onChange={(e) => form.setValue("customer_name", e.target.value)}
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none disabled:opacity-50 md:text-sm"
                          />
                          {errors.customer_name && (
                            <p className="text-cred text-sm mt-1">{errors.customer_name.message}</p>
                          )}
                      </div>
                      <div>
                          <label className="text-sm font-medium">Email Address</label>
                          <input
                              name="customer_email"
                              value={form.watch("customer_email")}
                              onChange={(e) => form.setValue("customer_email", e.target.value)}
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none disabled:opacity-50 md:text-sm"
                          />
                          {errors.customer_email && (
                            <p className="text-cred text-sm mt-1">{errors.customer_email.message}</p>
                          )}
                      </div>

                  <FormInputField name="guest" style="bg-cblack3" placeholder={"Enter Number of Guest"} form={form} type="number" label={"Number Of Guest"} required={true} />

                  <div className="grid grid-cols-4 gap-2">

                    <div className="col-span-1">
                      <FormInputField name="end_time" style="bg-cblack3" placeholder={"Select End Time"} form={form} type="time" label={"End Time"} />
                    </div>
                  </div>
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center">
                          <Switch
                            className="data-[state=unchecked]:bg-neutral-500"
                            checked={showIsChild}
                            onCheckedChange={(val) => setShowIsChild(val)}
                          />
                          <Label>Assign Staff</Label>
                        </div>
        
                        {showIsChild && (
                          <FormInputField
                            name="staff_id"
                            style="bg-cblack3"
                            placeholder={"Select Staff"}
                            form={form}
                            type="select"
                            parseAs="string"
                            selectOption={staffList?.map(item => ({
                              label: item?.user?.first_name + " " + item?.user?.last_name,
                              value: item?.user?.id,
                            }))}
                          />
                        )}
                      </div>

                  {/* show first validation error (if any) */}
                  {Object.keys(errors || {}).length > 0 && (
                    <p className="text-cred text-sm mt-2">{Object.values(errors)[0]?.message}</p>
                  )}

                  <div className="mt-4 flex gap-5 w-full">
                    <Button className="w-full bg-cred text-white" onClick={onClose}>
                        Cancel
                    </Button>

                    <Button type="submit" isLoading={loading} className="w-full bg-cyellow">
                        Confirm
                    </Button>
                  </div>

                  {/* debug preview of submitted payload (shows after clicking Confirm) */}
                  {/* {debugPayload && (
                    <pre className="mt-3 p-2 text-xs text-cwhite3 bg-cblack6 rounded max-h-40 overflow-auto">
                      {JSON.stringify(debugPayload, null, 2)}
                    </pre>
                  )} */}
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default QuickTableModal;
